import { useState, useEffect, useCallback, useRef } from 'react';
import Peer, { DataConnection } from 'peerjs';
import type { GameState, GameAction, LaneCommitment } from '../types/kotch';

// Message types for P2P communication
export type PeerMessageType =
  | 'game_start'       // Host sends initial game state
  | 'game_state'       // Host sends state updates
  | 'player_action'    // Guest sends actions to host
  | 'commitment'       // Player sends their commitment
  | 'player_info'      // Exchange player names
  | 'ready'            // Player ready confirmation
  | 'ping'             // Keep-alive
  | 'pong';            // Keep-alive response

export interface PeerMessage {
  type: PeerMessageType;
  payload?: unknown;
  timestamp: number;
}

export interface GameStartPayload {
  state: GameState;
  yourPlayerId: 1 | 2;
}

export interface CommitmentPayload {
  playerId: 1 | 2;
  commitment: LaneCommitment;
}

export interface ActionPayload {
  action: GameAction;
}

export interface PlayerInfoPayload {
  name: string;
}

export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'waiting'      // Host waiting for guest
  | 'connected'
  | 'error';

interface UsePeerConnectionOptions {
  onGameStart?: (state: GameState, playerId: 1 | 2) => void;
  onStateUpdate?: (state: GameState) => void;
  onCommitment?: (playerId: 1 | 2, commitment: LaneCommitment) => void;
  onAction?: (action: GameAction) => void;
  onReady?: (playerId: 1 | 2) => void;
  onPlayerInfo?: (name: string) => void;
}

// ICE server configuration for better NAT traversal
const ICE_SERVERS: RTCIceServer[] = [
  // Google's public STUN servers
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
  // Twilio's public STUN server
  { urls: 'stun:global.stun.twilio.com:3478' },
  // Open public TURN server (metered.ca free tier)
  {
    urls: 'turn:a.relay.metered.ca:80',
    username: 'e8dd65f92ae8d65678c8a827',
    credential: 'xxyaFe4+yWNbvhZb',
  },
  {
    urls: 'turn:a.relay.metered.ca:443',
    username: 'e8dd65f92ae8d65678c8a827',
    credential: 'xxyaFe4+yWNbvhZb',
  },
  {
    urls: 'turn:a.relay.metered.ca:443?transport=tcp',
    username: 'e8dd65f92ae8d65678c8a827',
    credential: 'xxyaFe4+yWNbvhZb',
  },
];

// PeerJS configuration
const PEER_CONFIG = {
  debug: 1,
  config: {
    iceServers: ICE_SERVERS,
    iceCandidatePoolSize: 10,
  },
};

export function usePeerConnection(options: UsePeerConnectionOptions = {}) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [roomCode, setRoomCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [remoteName, setRemoteName] = useState<string>('');
  const [connectionLost, setConnectionLost] = useState(false);

  // Use refs to avoid stale closures
  const peerRef = useRef<Peer | null>(null);
  const connectionRef = useRef<DataConnection | null>(null);
  const optionsRef = useRef(options);
  const statusRef = useRef<ConnectionStatus>('disconnected');

  // Heartbeat refs
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pongTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPongRef = useRef<number>(Date.now());

  // Retry refs
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Keep refs in sync
  optionsRef.current = options;

  const updateStatus = useCallback((newStatus: ConnectionStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
  }, []);

  // Generate a short room code
  const generateRoomCode = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing chars
    let code = 'KOTCH-';
    for (let i = 0; i < 4; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    return code;
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((data: PeerMessage) => {
    const opts = optionsRef.current;
    const conn = connectionRef.current;

    switch (data.type) {
      case 'game_start': {
        const payload = data.payload as GameStartPayload;
        opts.onGameStart?.(payload.state, payload.yourPlayerId);
        break;
      }
      case 'game_state': {
        const state = data.payload as GameState;
        opts.onStateUpdate?.(state);
        break;
      }
      case 'commitment': {
        const payload = data.payload as CommitmentPayload;
        opts.onCommitment?.(payload.playerId, payload.commitment);
        break;
      }
      case 'player_action': {
        const payload = data.payload as ActionPayload;
        opts.onAction?.(payload.action);
        break;
      }
      case 'ready': {
        const playerId = data.payload as 1 | 2;
        opts.onReady?.(playerId);
        break;
      }
      case 'player_info': {
        const payload = data.payload as PlayerInfoPayload;
        setRemoteName(payload.name);
        opts.onPlayerInfo?.(payload.name);
        break;
      }
      case 'ping': {
        conn?.send({ type: 'pong', timestamp: Date.now() });
        break;
      }
      case 'pong': {
        lastPongRef.current = Date.now();
        setConnectionLost(false);
        break;
      }
    }
  }, []);

  // Setup connection event handlers
  const setupConnection = useCallback((conn: DataConnection) => {
    conn.on('open', () => {
      console.log('[Kotch P2P] Connection opened');
      connectionRef.current = conn;
      updateStatus('connected');
      setError(null);
    });

    conn.on('data', (data) => {
      handleMessage(data as PeerMessage);
    });

    conn.on('close', () => {
      console.log('[Kotch P2P] Connection closed');
      connectionRef.current = null;
      // Only update to disconnected if we were connected
      if (statusRef.current === 'connected') {
        updateStatus('disconnected');
      }
    });

    conn.on('error', (err) => {
      console.error('[Kotch P2P] Connection error:', err);
      setError(err.message);
      updateStatus('error');
    });
  }, [handleMessage, updateStatus]);

  // Create a room (become host)
  const createRoom = useCallback(async (): Promise<string> => {
    const attemptCreate = (code: string, attempt: number): Promise<string> => {
      return new Promise((resolve, reject) => {
        updateStatus('connecting');
        setIsHost(true);
        setError(null);

        console.log(`[Kotch P2P] Creating room: ${code} (attempt ${attempt + 1}/${maxRetries + 1})`);

        const newPeer = new Peer(code, PEER_CONFIG);

        // Timeout for initial connection to signaling server
        const connectionTimeout = setTimeout(() => {
          console.log('[Kotch P2P] Connection to signaling server timed out');
          newPeer.destroy();
          if (attempt < maxRetries) {
            console.log('[Kotch P2P] Retrying...');
            // Generate new code for retry to avoid ID conflicts
            const newCode = generateRoomCode();
            resolve(attemptCreate(newCode, attempt + 1));
          } else {
            setError('Could not connect to server. Please try again.');
            updateStatus('error');
            reject(new Error('Connection timeout after retries'));
          }
        }, 8000);

        newPeer.on('open', (id) => {
          clearTimeout(connectionTimeout);
          console.log('[Kotch P2P] Peer opened with ID:', id);
          peerRef.current = newPeer;
          setRoomCode(code);
          retryCountRef.current = 0;
          updateStatus('waiting');
          resolve(code);
        });

        newPeer.on('connection', (conn) => {
          console.log('[Kotch P2P] Incoming connection from guest');
          setupConnection(conn);
        });

        newPeer.on('error', (err) => {
          clearTimeout(connectionTimeout);
          console.error('[Kotch P2P] Peer error:', err);

          // Check if it's a retriable error
          const isRetriable = err.type === 'network' ||
                              err.type === 'server-error' ||
                              err.type === 'socket-error' ||
                              err.type === 'socket-closed';

          if (isRetriable && attempt < maxRetries) {
            console.log('[Kotch P2P] Retriable error, attempting again...');
            newPeer.destroy();
            const newCode = generateRoomCode();
            resolve(attemptCreate(newCode, attempt + 1));
          } else {
            // ID taken - try with new code
            if (err.type === 'unavailable-id') {
              console.log('[Kotch P2P] Room code taken, generating new one...');
              newPeer.destroy();
              const newCode = generateRoomCode();
              resolve(attemptCreate(newCode, 0));
            } else {
              setError(err.message || 'Failed to create room');
              updateStatus('error');
              reject(err);
            }
          }
        });

        // Don't reset on 'disconnected' - it fires even during normal reconnection
        newPeer.on('disconnected', () => {
          console.log('[Kotch P2P] Peer disconnected from signaling server');
          // Only matters if we haven't established a connection yet
          if (statusRef.current === 'waiting' || statusRef.current === 'connecting') {
            // Try to reconnect to signaling server
            newPeer.reconnect();
          }
        });
      });
    };

    const code = generateRoomCode();
    return attemptCreate(code, 0);
  }, [generateRoomCode, setupConnection, updateStatus]);

  // Join an existing room (become guest)
  const joinRoom = useCallback(async (code: string): Promise<void> => {
    const attemptJoin = (attempt: number): Promise<void> => {
      return new Promise((resolve, reject) => {
        updateStatus('connecting');
        setIsHost(false);
        setError(null);
        setRoomCode(code);

        console.log(`[Kotch P2P] Joining room: ${code} (attempt ${attempt + 1}/${maxRetries + 1})`);

        const guestId = `${code}-guest-${Math.random().toString(36).slice(2, 6)}`;
        const newPeer = new Peer(guestId, PEER_CONFIG);

        let resolved = false;
        let timeoutId: NodeJS.Timeout;
        let peerOpenTimeout: NodeJS.Timeout;

        // Timeout for peer to open (signaling server connection)
        peerOpenTimeout = setTimeout(() => {
          if (!resolved) {
            console.log('[Kotch P2P] Peer open timeout');
            newPeer.destroy();
            if (attempt < maxRetries) {
              console.log('[Kotch P2P] Retrying join...');
              resolve(attemptJoin(attempt + 1));
            } else {
              setError('Could not connect to server. Please try again.');
              updateStatus('error');
              reject(new Error('Peer open timeout after retries'));
            }
          }
        }, 8000);

        newPeer.on('open', () => {
          clearTimeout(peerOpenTimeout);
          console.log('[Kotch P2P] Guest peer opened, connecting to host...');
          peerRef.current = newPeer;

          const conn = newPeer.connect(code, { reliable: true });
          setupConnection(conn);

          // Timeout for connection to host
          timeoutId = setTimeout(() => {
            if (!resolved && statusRef.current !== 'connected') {
              console.log('[Kotch P2P] Connection to host timeout');
              newPeer.destroy();
              if (attempt < maxRetries) {
                console.log('[Kotch P2P] Retrying join...');
                resolve(attemptJoin(attempt + 1));
              } else {
                setError('Connection timeout - room may not exist or host is unreachable');
                updateStatus('error');
                reject(new Error('Connection timeout'));
              }
            }
          }, 15000); // Longer timeout for P2P connection establishment

          conn.on('open', () => {
            console.log('[Kotch P2P] Connected to host!');
            resolved = true;
            retryCountRef.current = 0;
            clearTimeout(timeoutId);
            resolve();
          });

          conn.on('error', (err) => {
            console.error('[Kotch P2P] Connection error:', err);
            clearTimeout(timeoutId);
            if (!resolved) {
              newPeer.destroy();
              if (attempt < maxRetries) {
                console.log('[Kotch P2P] Retrying after connection error...');
                resolve(attemptJoin(attempt + 1));
              } else {
                reject(err);
              }
            }
          });
        });

        newPeer.on('error', (err) => {
          console.error('[Kotch P2P] Guest peer error:', err);
          clearTimeout(timeoutId);
          clearTimeout(peerOpenTimeout);

          // Handle specific PeerJS errors
          if (err.type === 'peer-unavailable') {
            // Room definitely doesn't exist - don't retry
            setError('Room not found. Check the code and try again.');
            updateStatus('error');
            if (!resolved) {
              reject(err);
            }
          } else {
            // Network/server errors - retry
            const isRetriable = err.type === 'network' ||
                                err.type === 'server-error' ||
                                err.type === 'socket-error' ||
                                err.type === 'socket-closed';

            if (isRetriable && attempt < maxRetries && !resolved) {
              console.log('[Kotch P2P] Retriable error, attempting again...');
              newPeer.destroy();
              resolve(attemptJoin(attempt + 1));
            } else if (!resolved) {
              setError(err.message || 'Failed to join room');
              updateStatus('error');
              reject(err);
            }
          }
        });

        newPeer.on('disconnected', () => {
          console.log('[Kotch P2P] Guest peer disconnected from signaling');
          // Try to reconnect if we haven't connected yet
          if (statusRef.current === 'connecting') {
            newPeer.reconnect();
          }
        });
      });
    };

    return attemptJoin(0);
  }, [setupConnection, updateStatus]);

  // Send a message to the connected peer
  const sendMessage = useCallback((type: PeerMessageType, payload?: unknown) => {
    const conn = connectionRef.current;
    if (conn && conn.open) {
      const message: PeerMessage = {
        type,
        payload,
        timestamp: Date.now(),
      };
      conn.send(message);
    } else {
      console.warn('[Kotch P2P] Cannot send message - no connection');
    }
  }, []);

  // Send game start (host only)
  const sendGameStart = useCallback((state: GameState, guestPlayerId: 1 | 2) => {
    const payload: GameStartPayload = {
      state,
      yourPlayerId: guestPlayerId,
    };
    sendMessage('game_start', payload);
  }, [sendMessage]);

  // Send state update (host only)
  const sendStateUpdate = useCallback((state: GameState) => {
    sendMessage('game_state', state);
  }, [sendMessage]);

  // Send commitment
  const sendCommitment = useCallback((playerId: 1 | 2, commitment: LaneCommitment) => {
    const payload: CommitmentPayload = { playerId, commitment };
    sendMessage('commitment', payload);
  }, [sendMessage]);

  // Send action (guest only)
  const sendAction = useCallback((action: GameAction) => {
    const payload: ActionPayload = { action };
    sendMessage('player_action', payload);
  }, [sendMessage]);

  // Send ready signal
  const sendReady = useCallback((playerId: 1 | 2) => {
    sendMessage('ready', playerId);
  }, [sendMessage]);

  // Send player info (name)
  const sendPlayerInfo = useCallback((name: string) => {
    const payload: PlayerInfoPayload = { name };
    sendMessage('player_info', payload);
  }, [sendMessage]);

  // Clear heartbeat timers
  const clearHeartbeat = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  // Start heartbeat
  const startHeartbeat = useCallback(() => {
    clearHeartbeat();
    lastPongRef.current = Date.now();
    setConnectionLost(false);

    // Send ping every 30 seconds
    pingIntervalRef.current = setInterval(() => {
      const conn = connectionRef.current;
      if (conn && conn.open) {
        console.log('[Kotch P2P] Sending ping');
        conn.send({ type: 'ping', timestamp: Date.now() });

        // Check for pong response after 10 seconds
        pongTimeoutRef.current = setTimeout(() => {
          const timeSinceLastPong = Date.now() - lastPongRef.current;
          if (timeSinceLastPong > 40000) { // No pong for 40 seconds (missed at least one ping cycle)
            console.log('[Kotch P2P] Connection appears lost - no pong received');
            setConnectionLost(true);
          }
        }, 10000);
      }
    }, 30000);
  }, [clearHeartbeat]);

  // Disconnect and cleanup
  const disconnect = useCallback(() => {
    console.log('[Kotch P2P] Disconnecting...');
    clearHeartbeat();
    connectionRef.current?.close();
    peerRef.current?.destroy();
    connectionRef.current = null;
    peerRef.current = null;
    updateStatus('disconnected');
    setRoomCode('');
    setIsHost(false);
    setRemoteName('');
    setError(null);
    setConnectionLost(false);
  }, [updateStatus, clearHeartbeat]);

  // Start heartbeat when connected
  useEffect(() => {
    if (status === 'connected') {
      startHeartbeat();
    } else {
      clearHeartbeat();
      setConnectionLost(false);
    }
  }, [status, startHeartbeat, clearHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHeartbeat();
      connectionRef.current?.close();
      peerRef.current?.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // State
    status,
    roomCode,
    error,
    isHost,
    remoteName,
    isConnected: status === 'connected',
    connectionLost,

    // Actions
    createRoom,
    joinRoom,
    disconnect,

    // Messaging
    sendGameStart,
    sendStateUpdate,
    sendCommitment,
    sendAction,
    sendReady,
    sendPlayerInfo,
  };
}
